// A small pixel burst where the user clicks empty space, with a quiet blip.
// Purely decorative: it never intercepts the click (see .sparkle in modelloop.css).

import { sounds } from "./sounds";
import { prefersReducedMotion } from "../prefs";

// Anything that does something, holds text worth selecting, or closes a layer
// when clicked is not "empty space".
const NOT_EMPTY = [
  "button", "a", "input", "textarea", "select", "label", "summary", "pre", "code", "img", "svg", "canvas",
  "[role='button']", "[role='switch']", "[role='option']", "[contenteditable='true']", "[onclick]",
  // This site's surfaces that hold content or close a layer
  "[data-no-sparkle]", ".tile", ".terminal", ".termwin", ".kpanic", ".modal-dialog", ".settings",
  ".search-box", ".menu",
  "[class*='overlay']", "[class*='backdrop']", "[class*='dismiss']",
].join(",");

// Eight directions for the burst: [x, y, shape]. Up/down/left/right are short
// lines, the diagonals single pixels, like a hit spark in an 8-bit game.
const BITS: [number, number, "h" | "v" | "dot"][] = [
  [1, 0, "h"], [-1, 0, "h"], [0, 1, "v"], [0, -1, "v"],
  [0.75, 0.75, "dot"], [-0.75, 0.75, "dot"], [0.75, -0.75, "dot"], [-0.75, -0.75, "dot"],
];

// The system setting or the Settings switch
const reducedMotion = { get matches() { return prefersReducedMotion(); } };

// True when the click landed on text inside the element, not the space around it.
function onText(target: Element, x: number, y: number): boolean {
  const range = document.createRange();
  for (const node of target.childNodes) {
    if (node.nodeType !== Node.TEXT_NODE || !node.textContent?.trim()) continue;
    range.selectNodeContents(node);
    for (const r of range.getClientRects()) {
      if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) return true;
    }
  }
  return false;
}

export function isEmptySpaceClick(e: MouseEvent): boolean {
  const target = e.target;
  if (!(target instanceof Element)) return false;
  if (target.closest(NOT_EMPTY)) return false;
  if (window.getSelection()?.toString()) return false; // finishing a text selection
  return !onText(target, e.clientX, e.clientY);
}

// `snes` colours each direction like the Super Famicom face buttons (see .sparkle--snes).
// `scale` is the page zoom, if the UI is zoomed (pointer coordinates are viewport px).
export function sparkleAt(x: number, y: number, opts: { snes?: boolean; scale?: number } = {}) {
  sounds.play("sparkle");
  if (reducedMotion.matches) return;

  const root = document.createElement("div");
  root.className = opts.snes ? "sparkle sparkle--snes" : "sparkle";
  const s = opts.scale ?? 1;
  root.style.left = `${Math.round(x / s)}px`;
  root.style.top = `${Math.round(y / s)}px`;
  for (const [dx, dy, shape] of BITS) {
    const bit = document.createElement("span");
    bit.className = `sparkle-bit sparkle-bit--${shape}`;
    bit.style.setProperty("--dx", String(dx));
    bit.style.setProperty("--dy", String(dy));
    root.append(bit);
  }
  document.body.append(root);
  setTimeout(() => root.remove(), 500);
}
